class ColorVariantPicker extends HTMLElement {
  #host = null;
  #variants = [];
  #swatches = [];
  #selectedValueEl = null;
  #productUrl = '';
  #optionPosition = 0;
  #optionKey = 'option1';

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#productUrl = this.dataset.productUrl || '';
    this.#optionPosition = parseInt(this.dataset.optionPosition, 10) || 0;
    this.#optionKey = `option${this.#optionPosition + 1}`;

    this.#variants = this.#parseVariants();
    this.#swatches = Array.from(this.querySelectorAll('.color-picker__swatch'));
    this.#selectedValueEl = this.querySelector('[data-selected-value]');

    this.#swatches.forEach((sw) => {
      sw.addEventListener('click', this.#onSwatchClick);
      sw.addEventListener('keydown', this.#onSwatchKeyDown);
    });

    // Cross-variant availability: when the size (or any other option) changes,
    // re-strike the colour swatches that have no available variant for it.
    this.#host = this.closest('main-product');
    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);

    // The update event only fires on change, so paint the initial state explicitly.
    const initialVariant = this.#getCurrentVariant();
    if (initialVariant) this.#syncColoursToSize(initialVariant);
  }

  disconnectedCallback() {
    this.#swatches.forEach((sw) => {
      sw.removeEventListener('click', this.#onSwatchClick);
      sw.removeEventListener('keydown', this.#onSwatchKeyDown);
    });
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (variant) this.#syncColoursToSize(variant);
  };

  // For each colour, find a variant with that colour AND the currently selected
  // non-colour options (e.g. the chosen size). Missing or unavailable -> crossed
  // out (is-oos). A colour with NO variants at all is never rendered as a swatch.
  #syncColoursToSize(variant) {
    this.#swatches.forEach((sw) => {
      const colorValue = sw.dataset.value;
      const match = this.#variants.find((v) =>
        v[this.#optionKey] === colorValue &&
        ['option1', 'option2', 'option3'].every((key) =>
          key === this.#optionKey ? true : v[key] === variant[key]
        )
      );
      if (!match) {
        // Colour doesn't come in the selected size at all -> hide it.
        sw.hidden = true;
        sw.classList.remove('is-oos');
      } else {
        // Exists: available -> normal, sold out -> crossed out. Applies to every
        // colour INCLUDING the current one (if the selected variant is sold out,
        // its own swatch is crossed out too).
        sw.hidden = false;
        sw.classList.toggle('is-oos', !match.available);
      }
    });
  }

  #parseVariants() {
    const node = this.closest('main-product')?.querySelector('[data-variants-data]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('color-picker: variants JSON parse failed', err);
      return [];
    }
  }

  #onSwatchClick = (event) => {
    const swatch = event.currentTarget;
    const newValue = swatch.dataset.value;
    if (!newValue) return;
    if (swatch.classList.contains('is-selected')) return;
    this.#selectValue(newValue);
  };

  #onSwatchKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.click();
      return;
    }

    // Roving tabindex skips colours hidden for the current size.
    const visible = this.#swatches.filter((sw) => !sw.hidden);
    const idx = visible.indexOf(event.currentTarget);
    if (idx === -1) return;

    let nextIdx = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIdx = (idx + 1) % visible.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIdx = (idx - 1 + visible.length) % visible.length;
    }
    if (nextIdx === null) return;
    event.preventDefault();
    visible[nextIdx].focus();
  };

  #selectValue(newValue) {
    const currentVariant = this.#getCurrentVariant();
    const nextVariant = this.#findMatchingVariant(newValue, currentVariant);

    if (!nextVariant) {
      console.error(`color-picker: no variant matches ${this.#optionKey}=${newValue}`);
      return;
    }

    this.#swatches.forEach((sw) => {
      const matches = sw.dataset.value === newValue;
      sw.classList.toggle('is-selected', matches);
      sw.setAttribute('aria-checked', matches ? 'true' : 'false');
      sw.setAttribute('tabindex', matches ? '0' : '-1');
    });

    if (this.#selectedValueEl) {
      this.#selectedValueEl.textContent = newValue;
    }

    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      composed: true,
      detail: {
        variant: nextVariant,
        optionChanged: this.#optionKey,
      },
    }));
  }

  #getCurrentVariant() {
    const hostVariant = this.closest('main-product')?.currentVariant;
    if (hostVariant) return hostVariant;
    // First paint: <main-product> may not be upgraded yet (block scripts run before
    // the section script), so host.currentVariant is undefined. Resolve from the URL.
    const urlId = parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
    if (urlId) {
      const match = this.#variants.find((v) => v.id === urlId);
      if (match) return match;
    }
    return this.#variants.find((v) => v.available) || this.#variants[0] || null;
  }

  #findMatchingVariant(newValue, currentVariant) {
    if (!currentVariant) {
      return this.#variants.find((v) => v[this.#optionKey] === newValue) || null;
    }

    const wantedOptions = ['option1', 'option2', 'option3'].map((key) => {
      if (key === this.#optionKey) return newValue;
      return currentVariant[key];
    });

    const exact = this.#variants.find((v) =>
      v.option1 === wantedOptions[0] &&
      v.option2 === wantedOptions[1] &&
      v.option3 === wantedOptions[2] &&
      v.available
    );
    const exactUnavailable = this.#variants.find((v) =>
      v.option1 === wantedOptions[0] &&
      v.option2 === wantedOptions[1] &&
      v.option3 === wantedOptions[2]
    );
    // Prefer the same size in the new colour. If that size doesn't exist in this
    // colour at all (e.g. clicking a crossed-out swatch), switch to the colour's
    // first available variant (then any) so the click still works; the size picker
    // re-syncs to the new size. Only falls back when the same size is truly absent.
    const colourAvailable = this.#variants.find((v) => v[this.#optionKey] === newValue && v.available);
    const colourAny = this.#variants.find((v) => v[this.#optionKey] === newValue);
    return exact || exactUnavailable || colourAvailable || colourAny || null;
  }
}

if (!customElements.get('color-variant-picker')) {
  customElements.define('color-variant-picker', ColorVariantPicker);
}
